import { request, requestForm, requestNoContent } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/**
 * Tình trạng bài viết. Số khớp với kiểu liệt kê ở máy chủ.
 *
 * "Hẹn giờ" không phải tình trạng riêng: đó là bài đã đăng có ngày đăng ở tương lai (cờ `isScheduled`).
 */
export const BlogPostStatus = {
  Draft: 1,
  Published: 2,
} as const;

export type BlogPostStatusValue = (typeof BlogPostStatus)[keyof typeof BlogPostStatus];

/** Một danh mục blog. */
export interface BlogCategory {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly description: string | null;
  readonly sortOrder: number;
  /** Số bài (kể cả nháp) đang thuộc danh mục. */
  readonly postCount: number;
}

/** Một bài viết trong danh sách — không kèm nội dung. */
export interface BlogPostSummary {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly excerpt: string;
  readonly categoryId: string | null;
  readonly categoryName: string | null;
  readonly authorName: string | null;
  readonly coverImageUrl: string | null;
  readonly status: BlogPostStatusValue;
  readonly publishedAt: string | null;
  /** Đã đăng nhưng ngày đăng còn ở tương lai — website chưa thấy. */
  readonly isScheduled: boolean;
  readonly updatedAt: string;
}

/** Đầy đủ một bài viết, kèm nội dung HTML đã được máy chủ làm sạch. */
export interface BlogPost extends BlogPostSummary {
  readonly contentHtml: string;
  /** Tiêu đề trên kết quả tìm kiếm; null là dùng tiêu đề bài. */
  readonly seoTitle: string | null;
  /** Mô tả trên kết quả tìm kiếm; null là dùng tóm tắt. */
  readonly metaDescription: string | null;
  /** Từ khóa chính — chỉ để chấm điểm SEO, không đưa ra website. */
  readonly focusKeyword: string | null;
  /** Mô tả ảnh bìa bằng chữ. */
  readonly coverImageAlt: string | null;
}

/** Điều kiện lọc danh sách bài viết. */
export interface BlogPostFilter {
  readonly searchTerm?: string;
  readonly categoryId?: string;
  readonly status?: BlogPostStatusValue;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung khi khai mới hoặc sửa một bài. */
export interface SaveBlogPostPayload {
  readonly title: string;
  /** Để trống thì máy chủ sinh từ tiêu đề. */
  readonly slug: string | null;
  readonly excerpt: string;
  readonly contentHtml: string;
  readonly categoryId: string | null;
  readonly authorName: string | null;
  readonly seoTitle: string | null;
  readonly metaDescription: string | null;
  readonly focusKeyword: string | null;
  readonly coverImageAlt: string | null;
}

/** Nội dung khi khai mới hoặc sửa một danh mục. */
export interface SaveBlogCategoryPayload {
  readonly name: string;
  readonly slug: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
}

const postRoute = '/api/v1/projects/blog-posts';
const categoryRoute = '/api/v1/projects/blog-categories';

/** Địa chỉ trang blog trên website — chỉ để hiện cho người viết thấy đường dẫn của bài. */
export const websiteBlogBaseUrl = 'https://mvalab.vn/blog';

function buildQueryString(filter: BlogPostFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.categoryId !== undefined) {
    parameters.set('categoryId', filter.categoryId);
  }

  if (filter.status !== undefined) {
    parameters.set('status', filter.status.toString());
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', filter.pageNumber.toString());
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', filter.pageSize.toString());
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách bài viết. */
export async function listBlogPosts(filter: BlogPostFilter): Promise<PagedResponse<BlogPostSummary>> {
  return await request<PagedResponse<BlogPostSummary>>(`${postRoute}${buildQueryString(filter)}`);
}

/** Lấy đầy đủ một bài viết. */
export async function getBlogPost(id: string): Promise<BlogPost> {
  return await request<BlogPost>(`${postRoute}/${id}`);
}

/** Khai một bài viết mới — luôn ở dạng nháp. */
export async function createBlogPost(payload: SaveBlogPostPayload): Promise<BlogPost> {
  return await request<BlogPost>(postRoute, { method: 'POST', body: payload });
}

/** Sửa nội dung một bài viết. */
export async function updateBlogPost(id: string, payload: SaveBlogPostPayload): Promise<BlogPost> {
  return await request<BlogPost>(`${postRoute}/${id}`, { method: 'PUT', body: payload });
}

/**
 * Đăng bài, hoặc đổi ngày đăng.
 *
 * Bỏ trống ngày đăng thì máy chủ giữ ngày cũ nếu có, không thì lấy thời điểm hiện tại.
 */
export async function publishBlogPost(id: string, publishedAt: string | null): Promise<BlogPost> {
  return await request<BlogPost>(`${postRoute}/${id}/publish`, {
    method: 'PUT',
    body: { publishedAt },
  });
}

/** Gỡ bài khỏi website, đưa về nháp. */
export async function unpublishBlogPost(id: string): Promise<BlogPost> {
  return await request<BlogPost>(`${postRoute}/${id}/unpublish`, { method: 'PUT' });
}

/** Xóa mềm một bài viết. */
export async function deleteBlogPost(id: string): Promise<void> {
  await requestNoContent(`${postRoute}/${id}`, { method: 'DELETE' });
}

/** Đuôi tệp ảnh được nhận — khớp quy tắc ở máy chủ. */
export const blogImageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'] as const;

/** Dung lượng ảnh tối đa: 5 MB. */
export const blogImageMaxBytes = 5 * 1024 * 1024;

/** Tải ảnh bìa lên, thay ảnh cũ nếu có. */
export async function uploadBlogCoverImage(id: string, file: File): Promise<BlogPost> {
  const form = new FormData();

  form.append('file', file);

  return await requestForm<BlogPost>(`${postRoute}/${id}/cover-image`, form);
}

/** Gỡ ảnh bìa. */
export async function deleteBlogCoverImage(id: string): Promise<BlogPost> {
  return await request<BlogPost>(`${postRoute}/${id}/cover-image`, { method: 'DELETE' });
}

/** Tải một ảnh lên để chèn vào nội dung bài; trả về địa chỉ công khai của ảnh. */
export async function uploadBlogContentImage(file: File): Promise<string> {
  const form = new FormData();

  form.append('file', file);

  const result = await requestForm<{ readonly url: string }>(`${postRoute}/images`, form);

  return result.url;
}

/** Toàn bộ danh mục, theo thứ tự hiển thị. */
export async function listBlogCategories(): Promise<readonly BlogCategory[]> {
  return await request<readonly BlogCategory[]>(categoryRoute);
}

/** Khai một danh mục mới. */
export async function createBlogCategory(payload: SaveBlogCategoryPayload): Promise<BlogCategory> {
  return await request<BlogCategory>(categoryRoute, { method: 'POST', body: payload });
}

/** Sửa một danh mục. */
export async function updateBlogCategory(id: string, payload: SaveBlogCategoryPayload): Promise<BlogCategory> {
  return await request<BlogCategory>(`${categoryRoute}/${id}`, { method: 'PUT', body: payload });
}

/** Xóa một danh mục — máy chủ từ chối khi danh mục còn bài. */
export async function deleteBlogCategory(id: string): Promise<void> {
  await requestNoContent(`${categoryRoute}/${id}`, { method: 'DELETE' });
}
